import { Box, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FC } from 'react';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import { FieldValidationError } from 'utilities/validation/validation';
import { IdentificationNumberField } from '../../../common/IdentificationNumberField';
import { Transferor } from '../../../types';
import {
  baseIdentificationTypeOptions,
  FOREIGN_TERRITORY_OPTIONS,
  TransferorType,
  TrusteePersonType,
} from '../../constants';

const getIdentificationTypeOptions = (transferorType: TransferorType | TrusteePersonType | string) => {
  if (
    transferorType === TransferorType.Trust ||
    transferorType === TransferorType.Entity ||
    transferorType === TrusteePersonType.LegalEntity
  ) {
    return baseIdentificationTypeOptions.filter((option) => option.value !== 'foreignPassport');
  } else {
    return baseIdentificationTypeOptions.filter((option) => option.value !== 'foreignEntityRegistrationNumber');
  }
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginBottom: theme.spacing(0),
    },
    box: {
      marginTop: theme.spacing(2),
    },
    dropdownError: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${theme.palette.error.main} !important`,
      },
    },
  }),
);

interface IdentificationFormProps {
  identification: Transferor['identification'];
  onUpdate: (identification: Transferor['identification']) => void;
  getFieldError: (fieldName: string) => { field: string; message: string } | undefined;
  clearFieldError?: (fieldName: string) => void;
  validationErrors?: FieldValidationError[];
  showValidation?: boolean;
  prefix?: string;
  fieldPrefix?: string; // For constructing validation field paths (e.g., 'identification' or 'trustees[0].identification')
  className?: string;
  transferorType?: TransferorType | TrusteePersonType | string;
  transferorIndex?: number;
  trusteeIndex?: number;
}

const IdentificationForm: FC<IdentificationFormProps> = ({
  identification,
  onUpdate,
  getFieldError,
  clearFieldError,
  validationErrors,
  showValidation = false,
  fieldPrefix = 'identification',
  className,
  transferorType,
  transferorIndex,
  trusteeIndex,
}) => {
  const classes = useStyles();

  // Generate data-testid prefix based on provided indices
  const getTestIdPrefix = () => {
    if (transferorIndex !== undefined && trusteeIndex !== undefined) {
      return `fincen-trustee-${transferorIndex}-${trusteeIndex}-identification`;
    } else if (transferorIndex !== undefined) {
      return `fincen-transferor-${transferorIndex}-identification`;
    }
    return 'fincen-transferor-identification';
  };
  const testIdPrefix = getTestIdPrefix();

  return (
    <div className={classes.section}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box>
            <Typography variant="h3">Form of Identification and Issuing Jurisdiction</Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <AgentNetDropdownSelector
            label="Type of Unique Identifying Number"
            name={`${fieldPrefix}.type`}
            value={identification?.type || ''}
            menuOption={(value: string) => {
              onUpdate({
                ...(identification ?? {}),
                type: value,
                number: '',
              });
              clearFieldError?.(`${fieldPrefix}.type`);
            }}
            options={getIdentificationTypeOptions(transferorType || '')}
            errs={validationErrors}
            showValidation={showValidation}
            error={
              !!getFieldError(`${fieldPrefix}.type`) ||
              (showValidation && !!validationErrors?.find((err) => err.field === `${fieldPrefix}.type`))
            }
            data-testid={`${testIdPrefix}-type-select`}
            aria-label="TransferorIdentificationType"
            variant="outlined"
            fullWidth
            required
          />
        </Grid>

        {identification?.type && identification.type !== 'noIdentification' && (
          <Grid item xs={6}>
            <IdentificationNumberField
              identificationType={identification.type}
              value={identification.number || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onUpdate({
                  ...(identification ?? {}),
                  number: e.target.value,
                });
                // Clear error when user types
                if (e.target.value && clearFieldError) {
                  clearFieldError(`${fieldPrefix}.number`);
                }
              }}
              error={
                !!getFieldError(`${fieldPrefix}.number`) ||
                (showValidation && !!validationErrors?.find((err) => err.field === `${fieldPrefix}.number`))
              }
              helperText={
                getFieldError(`${fieldPrefix}.number`)?.message ||
                (showValidation
                  ? validationErrors?.find((err) => err.field === `${fieldPrefix}.number`)?.message
                  : undefined)
              }
              name={`${fieldPrefix}.number`}
              aria-label="TransferorIdentificationNumber"
              data-testid={`${testIdPrefix}-number-input`}
              keyPrefix={fieldPrefix}
              required
              fullWidth
            />
          </Grid>
        )}

        {identification?.type &&
          (identification.type === 'foreignTaxId' ||
            identification.type === 'foreignPassport' ||
            identification.type === 'foreignEntityRegistrationNumber') && (
            <Grid item xs={6}>
              <AgentNetDropdownSelector
                label="Issuing Jurisdiction (if foreign)"
                name={`${fieldPrefix}.jurisdiction`}
                value={identification.jurisdiction || ''}
                menuOption={(value: string) => {
                  onUpdate({
                    ...(identification ?? {}),
                    jurisdiction: value,
                  });
                  clearFieldError?.(`${fieldPrefix}.jurisdiction`);
                }}
                options={FOREIGN_TERRITORY_OPTIONS}
                errs={validationErrors}
                showValidation={showValidation}
                error={
                  !!getFieldError(`${fieldPrefix}.jurisdiction`) ||
                  (showValidation && !!validationErrors?.find((err) => err.field === `${fieldPrefix}.jurisdiction`))
                }
                aria-label="TransferorIdentificationJurisdiction"
                data-testid={`${testIdPrefix}-jurisdiction-select`}
                required
                fullWidth
              />
            </Grid>
          )}
      </Grid>
    </div>
  );
};

export default IdentificationForm;
